import { useState, type FormEvent, type ReactElement } from 'react';
import { useNavigate } from 'react-router';

import { useAuth } from '../auth/AuthProvider.js';
import { useBranding } from '../branding/BrandingProvider.js';
import { Button } from '../components/Button.js';
import { Field } from '../components/Field.js';
import { Input } from '../components/Input.js';
import { PageHeader } from '../components/PageHeader.js';
import { ApiClientError, apiRequest } from '../lib/apiClient.js';

import { PasswordFields } from './PasswordFields.js';

/** Matches the other auth screens — browsed, not worked in, so 40px fields. */
const FIELD_CLASS = 'h-10 border-line-strong';

/**
 * Plan E6/E7. Reached voluntarily, or forced by `mustChangePassword` and by
 * `AUTH_PASSWORD_EXPIRED` — in the forced case `RequireAuth` keeps redirecting
 * here until it succeeds, so this page renders standalone rather than in the
 * shell.
 *
 * Which is why it carries the brand lockup itself: with no rail and no context
 * bar around it, a page that demands a new password and shows no sign of whose
 * panel it belongs to is the exact shape of a phishing screen.
 */
export function ChangePasswordPage(): ReactElement {
  const { mustChangePassword, clearMustChangePassword, refreshMe } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<ApiClientError | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await apiRequest('/auth/password/change', {
        method: 'POST',
        body: { currentPassword: current, newPassword: next },
      });
      clearMustChangePassword();
      await refreshMe();
      void navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof ApiClientError ? err : null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-surface-alt px-[var(--space-6)] py-[var(--space-10)]">
      <div className="flex w-full max-w-md flex-col gap-[var(--space-6)] card p-[var(--space-8)] shadow-[var(--elevation-2)]">
        <div className="flex flex-col gap-[var(--space-4)]">
          {branding?.logoUrl ? (
            // The alt text is the product name, not "logo": a screen reader
            // announcing "logo" tells the user nothing about whose panel this is.
            <img
              src={branding.logoUrl}
              alt={branding.productName}
              className="h-9 w-auto self-start"
            />
          ) : (
            <p className="text-sm font-semibold text-body">
              {branding?.productName ?? 'Connect Panel'}
            </p>
          )}
          <PageHeader
            title="Change your password"
            description={
              mustChangePassword
                ? 'You need to choose a new password before you can carry on.'
                : undefined
            }
          />
        </div>

        <form
          onSubmit={(e) => void onSubmit(e)}
          className="flex flex-col gap-[var(--space-4)]"
          noValidate
        >
          <Field label="Current password" required>
            {(props) => (
              <Input
                {...props}
                type="password"
                name="currentPassword"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className={FIELD_CLASS}
              />
            )}
          </Field>

          <PasswordFields
            error={error}
            value={next}
            confirmValue={confirm}
            onChange={setNext}
            onConfirmChange={setConfirm}
          />

          <Button
            type="submit"
            loading={busy}
            disabled={next !== confirm || next.length === 0}
            className="mt-[var(--space-2)] h-11 w-full font-semibold shadow-[var(--elevation-2)]"
          >
            Change password
          </Button>
        </form>
      </div>
    </main>
  );
}
